import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CalendarClock, Info, RefreshCw, ShieldAlert } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import PlanList from "@/components/subscription/PlanList";
import { useAuth } from "@/context/AuthContext";
import { ensureMidtransSnapReady } from "@/utils/midtrans";
import { useTranslation } from "react-i18next";

const API = `${process.env.REACT_APP_API_URL}/api`;

const PLAN_COLORS = {
  free: "text-muted-foreground",
  starter: "text-emerald-400",
  pro: "text-brand-primary",
  business: "text-purple-400",
  unlimited: "text-brand-primary",
};

export default function Subscription() {
  const { user, checkAuth } = useAuth();
  const { t, i18n } = useTranslation();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [processingPlanId, setProcessingPlanId] = useState(null);
  const locale = i18n.language?.startsWith("en") ? "en-US" : "id-ID";

  const currentPlan = user?.plan || status?.plan || "free";
  const planLabel = t(`dashboard.plan.label.${currentPlan}`, { defaultValue: currentPlan });
  const usageResetAt = user?.usage_reset_at || status?.usage_reset_at;
  const usageResetLabel = usageResetAt
    ? new Date(usageResetAt).toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" })
    : "—";
  const downgradeAtLabel = useMemo(() => {
    if (!status?.downgrade_at) return "—";
    try {
      return new Date(status.downgrade_at).toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" });
    } catch {
      return "—";
    }
  }, [status?.downgrade_at, locale]);

  const isUnlimited = currentPlan === "unlimited";
  const isFree = currentPlan === "free";
  const trialEndRaw = status?.trial_end || user?.trial_end;
  const trialEndDate = trialEndRaw ? new Date(trialEndRaw) : null;
  const isTrialActive =
    !!trialEndDate &&
    !Number.isNaN(trialEndDate.getTime()) &&
    trialEndDate > new Date() &&
    currentPlan !== "free";
  const trialEndLabel = isTrialActive
    ? trialEndDate.toLocaleString("id-ID", {
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "Asia/Jakarta",
      })
    : null;
  const hasPaidSubscription =
    Boolean(status?.has_paid_subscription) ||
    Boolean(user?.recurring_plan_id) ||
    (!isTrialActive && !isFree && !isUnlimited);
  const showTrialBanner = isTrialActive && !hasPaidSubscription;
  const canCancel = !isTrialActive && hasPaidSubscription && !isUnlimited && !isFree;
  const canBuyCredits = !isFree && !isTrialActive;

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/subscription/status`, { withCredentials: true });
      setStatus(res.data || {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("subscription.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const loadPlans = useCallback(async () => {
    setPlansLoading(true);
    try {
      const res = await axios.get(`${API}/subscription/plans`, { withCredentials: true });
      setPlans(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setPlans([]);
      toast.error(
        err?.response?.data?.detail ||
          t("pricing.loadFailed", { defaultValue: "Failed to load plans" }),
      );
    } finally {
      setPlansLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadStatus();
    loadPlans();
  }, [loadPlans, loadStatus]);

  const handleCancel = async () => {
    if (!window.confirm(t("dashboard.plan.cancelConfirm"))) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/subscription/cancel`, {}, { withCredentials: true });
      await checkAuth();
      await loadStatus();
      const effectiveAt = res?.data?.effective_at;
      if (effectiveAt) {
        const dateLabel = new Date(effectiveAt).toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" });
        toast.success(
          t("subscription.pendingDowngrade", {
            plan: t("dashboard.plan.label.free"),
            date: dateLabel,
          }),
        );
      } else {
        toast.success(t("dashboard.plan.cancelSuccess"));
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    } finally {
      setSaving(false);
    }
  };

  const handlePlanSelect = useCallback(
    async (plan) => {
      const planId = plan?.id;
      if (!planId || planId === currentPlan) return;

      setProcessingPlanId(planId);
      try {
        const res = await axios.post(
          `${API}/subscription/checkout`,
          { plan_id: planId, billing_cycle: "monthly" },
          { withCredentials: true },
        );
        const payload = res?.data || {};

        if (payload.downgrade_scheduled) {
          const effectiveLabel = payload.effective_at
            ? new Date(payload.effective_at).toLocaleDateString(locale, {
                day: "numeric",
                month: "long",
                year: "numeric",
              })
            : t("common.soon", { defaultValue: "soon" });
          toast.success(
            t("subscription.pendingDowngrade", {
              plan: t(`dashboard.plan.label.${payload.new_plan}`, {
                defaultValue: payload.new_plan,
              }),
              date: effectiveLabel,
            }),
          );
          await checkAuth();
          await loadStatus();
          await loadPlans();
          setProcessingPlanId(null);
          return;
        }

        if (payload.trial === true && payload.requires_confirmation) {
          if (!payload.order_id) {
            toast.error(t("common.error"));
            return;
          }
          await axios.post(
            `${API}/subscription/trial/confirm`,
            { order_id: payload.order_id },
            { withCredentials: true },
          );
          toast.success(
            t("dashboard.payment.trialActive", {
              defaultValue: "Trial activated successfully",
            }),
          );
          window.location.href = `/dashboard?payment=success&order_id=${encodeURIComponent(
            payload.order_id,
          )}`;
          return;
        }

        if (payload.snap_token) {
          try {
            await ensureMidtransSnapReady();
          } catch {
            toast.error(
              t("pricing.snapUnavailable", {
                defaultValue: "Payment popup unavailable. Please try again.",
              }),
            );
            return;
          }

          window.snap.pay(payload.snap_token, {
            onSuccess: () => {
              window.location.href = `/dashboard?payment=pending&order_id=${encodeURIComponent(
                payload.order_id,
              )}`;
            },
            onPending: () => {
              window.location.href = `/dashboard?payment=pending&order_id=${encodeURIComponent(
                payload.order_id,
              )}`;
            },
            onError: () => {
              window.location.href = `/dashboard?payment=failed&order_id=${encodeURIComponent(
                payload.order_id,
              )}`;
            },
            onClose: () => {
              toast.info(
                t("pricing.paymentCancelled", {
                  defaultValue: "Pembayaran dibatalkan",
                }),
              );
              setProcessingPlanId(null);
            },
          });
          return;
        }

        if (payload.redirect_url) {
          window.location.href = payload.redirect_url;
          return;
        }

        toast.error(
          t("pricing.snapUnavailable", {
            defaultValue: "Payment popup unavailable. Please try again.",
          }),
        );
      } catch (err) {
        toast.error(err?.response?.data?.detail || t("common.error"));
      } finally {
        setProcessingPlanId(null);
      }
    },
    [checkAuth, currentPlan, loadPlans, loadStatus, locale, t],
  );

  return (
    <main className="min-h-screen" data-testid="subscription-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6">
          <ArrowLeft className="w-4 h-4" />
          {t("nav.dashboard")}
        </Link>

        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{t("nav.account")}</span>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mt-1">{t("subscription.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("subscription.subtitle")}</p>
        </motion.div>

        {loading ? (
          <div className="rounded-md border border-border bg-card p-8 flex items-center justify-center">
            <RefreshCw className="w-5 h-5 animate-spin text-brand-primary" />
          </div>
        ) : (
          <div className="space-y-5">
            {showTrialBanner && (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-md border border-sky-500/35 bg-sky-500/10 p-4"
                data-testid="subscription-trial-banner"
              >
                <div className="flex items-start gap-2.5">
                  <Info className="w-4 h-4 text-sky-300 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-sky-100">
                  {t("subscription.trialBanner", {
                    defaultValue:
                        `Kamu sedang dalam masa trial ${planLabel} selama 7 hari (gratis). Trial berakhir ${trialEndLabel}. Setelah trial berakhir, akun akan kembali ke paket Free.`,
                    plan: planLabel,
                    date: trialEndLabel,
                  })}
                  </p>
                </div>
                <Link to="/pricing" className="inline-block mt-3">
                  <Button size="sm" className="rounded-sm bg-brand-primary hover:bg-brand-primary/90 text-white">
                    {t("subscription.trialBannerCta", { defaultValue: "Pilih Paket Berbayar" })}
                  </Button>
                </Link>
              </motion.div>
            )}

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-md border border-border bg-card p-5"
            >
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div>
                  <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{t("subscription.currentPlan")}</p>
                  <p className={`font-heading text-2xl font-semibold mt-1 ${PLAN_COLORS[currentPlan] || "text-foreground"}`}>
                    {planLabel}
                  </p>
                  {showTrialBanner && trialEndLabel && (
                    <span className="inline-flex items-center rounded-sm border border-sky-500/40 bg-sky-500/10 px-2 py-1 mt-2 text-xs font-medium text-sky-100">
                      {t("subscription.currentPlanStatus.trial", {
                        defaultValue: "Trial aktif hingga {{date}}",
                        date: trialEndLabel,
                      })}
                    </span>
                  )}
                  {!showTrialBanner && hasPaidSubscription && (
                    <span className="inline-flex items-center rounded-sm border border-verdict-real/35 bg-verdict-real/10 px-2 py-1 mt-2 text-xs font-medium text-verdict-real">
                      {t("subscription.currentPlanStatus.paid", {
                        defaultValue: "Langganan aktif hingga {{date}}",
                        date: usageResetLabel,
                      })}
                    </span>
                  )}
                  <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                    <p className="inline-flex items-center gap-1.5">
                      <CalendarClock className="w-4 h-4" />
                      {t("subscription.resetAt", { date: usageResetLabel })}
                    </p>
                    {status?.pending_downgrade && (
                      <p className="inline-flex items-center gap-1.5 text-verdict-uncertain">
                        <ShieldAlert className="w-4 h-4" />
                        {t("subscription.pendingDowngrade", {
                          plan: t(`dashboard.plan.label.${status.pending_downgrade}`, { defaultValue: status.pending_downgrade }),
                          date: downgradeAtLabel,
                        })}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                <Link to="/pricing">
                  <Button className="rounded-sm bg-brand-primary hover:bg-brand-primary/90 text-white">
                    {isFree ? t("dashboard.plan.upgrade") : t("dashboard.plan.change")}
                  </Button>
                </Link>
                <Link to="/payment-history">
                  <Button variant="outline" className="rounded-sm">
                    {t("nav.paymentHistory")}
                  </Button>
                </Link>
                {canCancel && (
                  <Button
                    variant="outline"
                    className="rounded-sm border-verdict-fake/40 text-verdict-fake hover:bg-verdict-fake/10"
                    onClick={handleCancel}
                    disabled={saving}
                    data-testid="subscription-cancel-btn"
                  >
                    {saving ? t("common.loading") : t("dashboard.plan.cancelSub")}
                  </Button>
                )}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="rounded-md border border-border bg-card p-5"
              data-testid="subscription-credits-section"
            >
              <h2 className="font-heading text-lg font-semibold">
                {t("dashboard.credits.title")}
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                {canBuyCredits
                  ? t("subscription.creditsDesc", {
                      defaultValue: "Butuh kuota tambahan? Beli kredit langsung dari dashboard.",
                    })
                  : t("dashboard.credits.trialLocked", {
                      defaultValue: "Kredit hanya tersedia untuk langganan berbayar aktif",
                    })}
              </p>
              {canBuyCredits ? (
                <Link to="/dashboard" className="inline-block mt-3">
                  <Button
                    size="sm"
                    className="rounded-sm bg-brand-primary hover:bg-brand-primary/90 text-white"
                  >
                    {t("dashboard.credits.buy")}
                  </Button>
                </Link>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  disabled
                  className="rounded-sm mt-3 pointer-events-none"
                >
                  {t("dashboard.credits.buy")}
                </Button>
              )}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.08 }}
              className="rounded-md border border-border bg-card p-5"
              data-testid="subscription-plan-list-section"
            >
              <div className="mb-3">
                <h2 className="font-heading text-lg font-semibold">
                  {t("subscription.managePlans")}
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  {t("pricing.appSubtitle", {
                    defaultValue:
                      "Pilih paket untuk upgrade atau downgrade. Paket aktif ditandai otomatis.",
                  })}
                </p>
              </div>
              <PlanList
                plans={plans}
                currentPlan={currentPlan}
                loading={plansLoading}
                processingPlanId={processingPlanId}
                onPlanSelect={handlePlanSelect}
              />
              <div className="mt-4 flex justify-center">
                <Link
                  to="/pricing"
                  className="inline-flex items-center gap-2 text-sm font-medium text-brand-primary hover:text-brand-primary/80 transition-colors"
                  data-testid="subscription-go-pricing-detail"
                >
                  {t("pricing.compare.link", {
                    defaultValue: "Lihat perbandingan fitur lengkap",
                  })}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.12 }}
              className="rounded-md border border-border bg-card p-5"
            >
              <h2 className="font-heading text-lg font-semibold mb-2">{t("subscription.howTo")}</h2>
              <ul className="text-sm text-muted-foreground space-y-1.5 list-disc pl-5">
                <li>{t("subscription.step.upgrade")}</li>
                <li>{t("subscription.step.downgrade")}</li>
                <li>{t("subscription.step.cancel")}</li>
              </ul>
            </motion.div>
          </div>
        )}
      </div>
    </main>
  );
}
